import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OnboardingFacade } from '../../../../../Facades/onboarding.facade';
import {
  CheckoutPreview,
  CheckoutPreviewTrial,
} from '../../../../../Interfaces/checkout-preview.interface';
import { PageState } from '../../../Types/page-state.type';

@Component({
  selector: 'app-review',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './review.component.html',
  styleUrl: './review.component.scss',
})
export class ReviewComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly onboardingFacade = inject(OnboardingFacade);
  private readonly translate = inject(TranslateService);

  pageState = signal<PageState>('verifying');
  preview = signal<CheckoutPreview | null>(null);
  isProcessingPayment = signal(false);
  errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    this.resolveEntryState();
  }

  private resolveEntryState(): void {
    this.onboardingFacade.get({ skipLoading: true }).subscribe({
      next: (onboarding) => {
        if (!onboarding?.plan_data) {
          this.router.navigate(['/setup/plan-selection']);
          return;
        }

        if (!onboarding.admin_consent_granted_at) {
          this.router.navigate(['/setup/connect-intune']);
          return;
        }

        this.loadPreview();
      },
      error: () => {
        this.router.navigate(['/setup/plan-selection']);
      },
    });
  }

  private loadPreview(): void {
    this.pageState.set('verifying');
    this.errorMessage.set(null);

    this.onboardingFacade.getCheckoutPreview({ skipLoading: true }).subscribe({
      next: (preview) => {
        this.preview.set(preview);
        this.pageState.set('ready');
      },
      error: () => {
        this.errorMessage.set(
          this.translate.instant('onboarding.setup.review.errors.loadFailed')
        );
        this.pageState.set('error');
      },
    });
  }

  retry(): void {
    this.loadPreview();
  }

  formatAmount(unitAmount: number): string {
    return Number.isInteger(unitAmount) ? String(unitAmount) : unitAmount.toFixed(2);
  }

  getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = { eur: '€', usd: '$', gbp: '£' };
    return symbols[currency.toLowerCase()] ?? currency.toUpperCase();
  }

  money(amount: number, currency: string): string {
    return this.getCurrencySymbol(currency) + this.formatAmount(amount);
  }

  stubWindowDays(trial: CheckoutPreviewTrial): number {
    const from = Date.parse(trial.first_invoice_at);
    const to = Date.parse(trial.billing_starts_at);

    if (Number.isNaN(from) || Number.isNaN(to)) {
      return 0;
    }

    return Math.max(0, Math.round((to - from) / 86400000));
  }

  offerLabelKey(offerType: string): string {
    return offerType === 'commit12'
      ? 'onboarding.setup.review.offer.commit12'
      : 'onboarding.setup.review.offer.monthly';
  }

  onBack(): void {
    this.router.navigate(['/setup/connect-intune']);
  }

  onAddPaymentMethod(): void {
    this.isProcessingPayment.set(true);
    this.errorMessage.set(null);

    this.onboardingFacade.initiateCheckout().subscribe({
      next: (result) => {
        window.location.href = result.checkout_url;
      },
      error: (error: Error) => {
        this.isProcessingPayment.set(false);
        this.errorMessage.set(
          error.message ||
            this.translate.instant('onboarding.setup.review.errors.checkoutFailed')
        );
      },
    });
  }
}
